import { Injectable } from '@angular/core';
import { Analysis } from '../../models/models';
import { AppEventService } from '../../app/app-events';
import { BhutilProvider } from '../bhutil/bhutil';

/*
  Generated class for the AnalysisProvider provider.

  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
*/
@Injectable({
    providedIn: 'root'
})
export class AnalysisHolderProvider {

  private currentBeerId: string = null;

  // key = beerId
  // value = Map<string, Analysis> where string is the type of analysis ("my", "local", "beerhive", "brewer")
  private analyses: Map<string,Map<string,Analysis>> = new Map<string, Map<string,Analysis>>();
  private beerstats: Map<string,any> = new Map<string,any>();
  private similarbeers: Map<string,any> = new Map<string,any>();
  // keep track of when each beer is loaded
  private lastLoadedDates: Map<string,Date> = new Map<string,Date>();

  loadedMyAnalysis: Analysis;
  localAnalysis: Analysis;
  beerhiveAnalysis: Analysis;
  localChangeDetected: boolean;
  shouldCheck: boolean;

  constructor(private events: AppEventService, private bhutil: BhutilProvider) {
    this.localChangeDetected = false;
    this.shouldCheck = true;
  }

  public loadAnalyses(userId: string, beerId: string, analyses: Map<string,any>, beerstats: any, similarbeers: any) {
    this.log("loadAnalyses for beer with id " + beerId);
    // create an entry for this beer if not yet existing
    if (!this.analyses.get(beerId) || !this.bhutil.isToday(this.lastLoadedDates.get(beerId))) {
      this.analyses.set(beerId,new Map<string,Analysis>());
    }
    // load all analyses in the entry
    analyses.forEach((value: any, key: string, map: Map<string,any>) => {
      //
      // immediately keep a copy of "my" analysis and prepare a clone for local editing
      if (key == "my") {
        this.analyses.get(beerId).set("loadedMy",value);
        if (!this.analyses.get(beerId).has("my"))
          this.analyses.get(beerId).set("my",{ ...value });
      }
      else {
        this.analyses.get(beerId).set(key,value);
      }
    });
    // if no "my" analysis was loaded, immediately set a local analysis
    if (!this.analyses.get(beerId).has("my")) {
        this.analyses.get(beerId).set("my", this.createNewLocalAnalysis(userId, beerId));
    }
    this.beerstats.set(beerId, beerstats);
    this.similarbeers.set(beerId, similarbeers);
    this.lastLoadedDates.set(beerId, new Date());
    this.currentBeerId = beerId;
  }
  public replaceMyAnalysis(analysis: Analysis): void {
    this.analyses.get(this.currentBeerId).set("loadedMy",analysis);
    this.analyses.get(this.currentBeerId).set("my",{ ...analysis });
  }
  public deactivateBeer(): void {
    this.log("deactivateBeer: deactivating beer with id " + this.currentBeerId);
    this.currentBeerId = null;
  }
  public hasActiveBeer(): boolean {
    return (this.currentBeerId && this.currentBeerId != null);
  }
  public getActiveBeer(): string {
    if (this.currentBeerId && this.currentBeerId != null)
      return this.currentBeerId;
    else
      return "";
  }

  public hasBeer(beerId: string): boolean {
    return this.analyses.has(beerId) && this.bhutil.isToday(this.lastLoadedDates.get(beerId));
  }
  public switchToBeer(beerId: string) {
    if (this.hasBeer(beerId)) {
      this.log("switchToBeer: switching to beer with id " + beerId);
      this.currentBeerId = beerId;
    }
    else {
      this.log("switchToBeer: Beer with id " + beerId + " is not loaded, throwing error");
      throw new Error("Beer with id " + beerId + " is not loaded");
    }
  }
  public hasAnalysis(type: string): boolean {
    return this.analyses.get(this.currentBeerId).has(type) && this.bhutil.isToday(this.lastLoadedDates.get(this.currentBeerId));
  }
  public hasLoadedMyAnalysis() {
    return this.hasAnalysis("loadedMy");
  }
  public hasMyAnalysis() {
    return this.hasAnalysis("my");
  }
  public hasBeerhiveAnalysis() {
    return this.hasAnalysis("beerhive");
  }
  public hasBrewerAnalysis() {
    return this.hasAnalysis("brewer");
  }
  public getAnalysis(type: string): Analysis {
    if (this.hasAnalysis(type))
      return this.analyses.get(this.currentBeerId).get(type);
    return null;
  }
  public getLoadedMyAnalysis(): Analysis {
    return this.getAnalysis("loadedMy");
  }
  public getMyAnalysis() {
    return this.getAnalysis("my");
  }
  public getBeerhiveAnalysis() {
    return this.getAnalysis("beerhive");
  }
  public getBrewerAnalysis() {
    return this.getAnalysis("brewer");
  }
  public getBeerStats(): any {
      return this.beerstats.get(this.currentBeerId);
  }
  public getSimilarBeers(): any {
      return this.similarbeers.get(this.currentBeerId);
  }
  public setSimilarBeers(beerId: string, venueId: string, similarbeers: any) {
    if (venueId != null) {
      this.log("setSimilarBeers: venue selection is unsupported")
      throw new Error("AnalysisHolderComponent.setSimilarBeers: venue selection is unsupported")
    }
    this.similarbeers.set(beerId, similarbeers);
  }

  private stringDiff(s1: string | undefined, s2: string | undefined): boolean {
    if (s1 && s2) return s1 != s2;
    if (s1 && !s2) return s1 != null && s1 != "";
    if (!s1 && s2) return s2 != null && s2 != "";
    return false;
  }
  private numDiff(n1: number | undefined, n2: number | undefined): boolean {
    if (n1 && n2) return n1 != n2;
    if (n1 && !n2) return true;
    if (!n1 && n2) return true;
    return false;
  }
  hasDifferences() {
    let diffCount = 0;
    let myAnalysis = this.getMyAnalysis();
    let loadedMyAnalysis = this.getLoadedMyAnalysis();

    if (this.hasLoadedMyAnalysis()) {
      if (this.hasMyAnalysis() && this.stringDiff(myAnalysis.notes, loadedMyAnalysis.notes))
        diffCount++;
      //sweet
      if (this.hasMyAnalysis() && loadedMyAnalysis.sweet !== undefined && myAnalysis.sweet != loadedMyAnalysis.sweet)
        diffCount++;
      //sour
      if (this.hasMyAnalysis() && loadedMyAnalysis.sour !== undefined && myAnalysis.sour != loadedMyAnalysis.sour)
        diffCount++;
      //bitter
      if (this.hasMyAnalysis() && loadedMyAnalysis.bitter !== undefined && myAnalysis.bitter != loadedMyAnalysis.bitter)
        diffCount++;
      //fruity
      if (this.hasMyAnalysis() && loadedMyAnalysis.fruity !== undefined && myAnalysis.fruity != loadedMyAnalysis.fruity)
        diffCount++;
      //hoppy
      if (this.hasMyAnalysis() && loadedMyAnalysis.hoppy !== undefined && myAnalysis.hoppy != loadedMyAnalysis.hoppy)
        diffCount++;
      //malty
      if (this.hasMyAnalysis() && loadedMyAnalysis.malty !== undefined && myAnalysis.malty != loadedMyAnalysis.malty)
        diffCount++;

      //COLOUR
      if (this.hasMyAnalysis() && myAnalysis.colour != loadedMyAnalysis.colour)
        diffCount++;

      //MOUTHFEEL
      if (this.hasMyAnalysis() && this.numDiff(loadedMyAnalysis.intensity?.valueOf(), myAnalysis.intensity?.valueOf()))
        diffCount++;
      if (this.hasMyAnalysis() && this.numDiff(loadedMyAnalysis.body?.valueOf(), myAnalysis.body?.valueOf()))
        diffCount++;
      if (this.hasMyAnalysis() && this.numDiff(loadedMyAnalysis.astringent?.valueOf(), myAnalysis.astringent?.valueOf()))
        diffCount++;
      if (this.hasMyAnalysis() && this.numDiff(loadedMyAnalysis.carbonation?.valueOf(), myAnalysis.carbonation?.valueOf()))
        diffCount++;
      if (this.hasMyAnalysis() && this.numDiff(loadedMyAnalysis.alcohol?.valueOf(), myAnalysis.alcohol?.valueOf()))
        diffCount++;

      let kstring1 = "";//local version
      let kstring2 = "";//api version

      //TODO ALL binary aspects (AROMAS)
      //kstring1, local analysis
      //this.apiAnalysis.characteristicsvalues.sort(this.comparename);
      if (myAnalysis.characteristicsvalues !== undefined && myAnalysis.characteristicsvalues.length != 0) {
        myAnalysis.characteristicsvalues = myAnalysis.characteristicsvalues.sort((a, b) => {
          if (a.name < b.name)
            return -1;
          if (a.name > b.name)
            return 1;
          return 0;
        });

        for (let theItem of myAnalysis.characteristicsvalues) {
          let k = theItem.name;//.characteristic.key;
          let v = theItem.value;//characteristic.value;
          if (k === "colour") {
            let colour1 = v.valueOf();
          }
          else if (k === "astringent") {
            let a1 = v != null ? v.valueOf() : null;
            let a2 = loadedMyAnalysis.astringent;
            if (a1 != a2)
              diffCount++;
          }
          else if (k === "body") {
            let b1 = v != null ? v.valueOf() : null;
            let b2 = loadedMyAnalysis.body;
            if (b1 != b2)
              diffCount++;
          }
          else if (k === "intensity") {
            let i1 = v != null ? v.valueOf() : null;
            let i2 = loadedMyAnalysis.intensity;
            if (i1 != i2)
              diffCount++;
          }
          else if (k === "carbonation") {
            let carbonation1 = v != null ? v.valueOf() : null;
            let carbonation2 = loadedMyAnalysis.carbonation;
            if (carbonation1 != carbonation2)
              diffCount++;
          }
          else if (k === "alcohol") {
            let alcohol1 = v != null ? v.valueOf() : null;
            let alcohol2 = loadedMyAnalysis.alcohol;
            if (alcohol1 != alcohol2)
              diffCount++;
          }
          else {
            //debugger;
            let kindex = k.indexOf("_");
            //if (kindex != -1 && v != 0) {
            if (kindex != -1 && v != 0) { // we also need the case where v == 0, when we are removing items
              //UPDATE: for the case where we are adding and then removing the same item, we need to SKIP the items with v == 0!!! (BEERHIVE-105)
              kstring1 = kstring1 + k + ":" + v + "|";
            }
          }
        }
        kstring1 = kstring1.substr(0, kstring1.length - 1);
      }

      if (loadedMyAnalysis.characteristicsvalues !== undefined && loadedMyAnalysis.characteristicsvalues.length != 0) {
        loadedMyAnalysis.characteristicsvalues.sort((a, b) => {
          if (a.name < b.name)
            return -1;
          if (a.name > b.name)
            return 1;
          return 0;
        });

        for (let theItem of loadedMyAnalysis.characteristicsvalues) {
          let k = theItem.name;
          let v = theItem.value;
          let kindex = k.indexOf("_");
          if (kindex != -1 && v != 0) {
            kstring2 = kstring2 + k + ":" + v + "|";
          }
        }
        kstring2 = kstring2.substr(0, kstring2.length - 1);
      }

      //debugger;
      // compare kstring1 and kstring2
      if (kstring1 !== kstring2) {
        diffCount++;
      }

      // RATING
      // ATTENTION: totalexperience for local, totalexperience for api!
      //debugger;

      if (this.hasMyAnalysis()) {
        if (myAnalysis.totalexperience != loadedMyAnalysis.totalexperience) {
          diffCount++;
        }
      }
    }
    if ((!this.hasLoadedMyAnalysis()) && this.hasMyAnalysis() && this.myAnalysisValid()) {
      // there is only a local analysis, so we will be adding an analysis instead of updating an existing analysis
      this.log("we will create a new analysis once we press the button");
      return true;
    }
    else if (diffCount > 0) {
      // there is at least one difference!
      return true;
    }
    return false;
  }

  myAnalysisValid() {
    // check if at least one field of myAnalysis holds a valid value
    let myAnalysis = this.getMyAnalysis();
    let validCount = 0;
    if (myAnalysis.alcohol !== undefined){
      validCount++;
    }
    if (myAnalysis.astringent !== undefined) {
      validCount++;
    }
    if (myAnalysis.body !== undefined) {
      validCount++;
    }
    if (myAnalysis.carbonation !== undefined) {
      validCount++;
    }
    if (myAnalysis.intensity !== undefined) {
      validCount++;
    }
    if (myAnalysis.bitter != -1) {
      validCount++;
    }
    if (myAnalysis.fruity != -1) {
      validCount++;
    }
    if (myAnalysis.hoppy != -1) {
      validCount++;
    }
    if (myAnalysis.malty != -1) {
      validCount++;
    }
    if (myAnalysis.sour != -1) {
      validCount++;
    }
    if (myAnalysis.sweet != -1) {
      validCount++;
    }
    if (myAnalysis.totalexperience != -1 && myAnalysis.totalexperience != 0) {
      validCount++;
    }
    if (myAnalysis.notes !== "") {
      validCount++;
    }
    if (myAnalysis.characteristics.length != 0) {
      validCount++;
    }
    if (myAnalysis.colour != 0) {
      validCount++;
    }
    if (myAnalysis.characteristicsvalues !== undefined) {
      if(myAnalysis.characteristicsvalues.length > 0) {
        for (let aChar of myAnalysis.characteristicsvalues) {
          if(aChar.value != 0)
            validCount++;
        }
        
      } 
    }
      
    //debugger;
    if (validCount > 0)
      return true;
    return false;
  }

  comparename(a, b) {
    //debugger;
    const nameA = a.name;//.toUpperCase()
    const nameB = b.name;//.toUpperCase();

    let comparison = 0;
    if (nameA > nameB) {
      comparison = 1;
    } else if (nameA < nameB) {
      comparison = -1;
    }
    return comparison;
  }

  cancelPendingSave() {
    this.localChangeDetected = false;
  }

  checkForDifferences() {
    if (!this.shouldCheck) {
      this.log("should not check for differences...");
      return;
    }
    let bHasDifferences = this.hasDifferences();
    //debugger;
    if (bHasDifferences) {
      this.events.publish("show:savebutton", true);
      this.events.publish("set:pendingsave", true);
      this.localChangeDetected = true;
    }
    else {
      this.events.publish("show:savebutton", false);
      this.events.publish("set:pendingsave", false);
      this.localChangeDetected = false;
    }
  }

  createNewLocalAnalysis(theUserId, beerId) {

    // set all default values
    let latitude = 0;
    let longitude = 0;
    let beer_id = beerId;
    let beer_characteristics = [];
    let beer_characteristics_values = [];

    let notes = "";
    let totalexperience = 0;
    let sweet = -1;//was 0
    let sour = -1;//was 0
    let bitter = -1;//was 0
    let malty = -1;//was 0
    let fruity = -1;//was 0
    let hoppy = -1;//was 0
    let colour = 0;
    let newAnalysis = new Analysis(theUserId, theUserId, latitude, longitude, beer_id, beer_characteristics, notes,totalexperience, sweet, sour, bitter, malty, fruity, hoppy, colour);
    newAnalysis.characteristicsvalues = beer_characteristics_values;
    newAnalysis.astringent = -1;
    newAnalysis.alcohol = -1;
    newAnalysis.intensity = -1;
    // clone this local file
    this.localAnalysis = { ...newAnalysis };
    return { ...newAnalysis };
  }

  cloneApiAnalysis() {
    //debugger;
    this.localAnalysis = { ...this.getLoadedMyAnalysis() };
  }

  private log(s: string): void {
    // console.log("AnalysisHolderProvider: " + s);
  }
}
